import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, MapPin, Calendar, Users, Search, Loader2, Tag, Shield, Star } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { hotelInfo, heroImages as fallbackHeroImages } from '../data/mock';
import { useBooking } from '../context/BookingContext';
import { useLanguage } from '../context/LanguageContext';
import { trackPageView } from '../hooks/useAnalytics';
import { langPath } from '../utils/siteNav';
import { optimized, srcSetFor } from '../utils/img';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Preferred gallery categories (in this order) — Hero shows a curated
// slideshow of the most photogenic sections of the hotel.
const HERO_CATEGORY_ORDER = ['Pool', 'Exterior', 'Terrace', 'Rooms', 'Restaurant', 'Apartment'];
const MAX_HERO_IMAGES = 6;

// Dynamic Hero — bucket the visitor's local hour so first-impression image
// matches the time-of-day mood. Sunrise/golden-hour visits open with the
// warmest exterior; night visits open with the pool bar & interior glow.
const heroBucket = (hour) => {
  if (hour >= 5 && hour < 10)  return { key: 'morning',  labels: ['Exterior', 'Terrace', 'Pool'] };
  if (hour >= 10 && hour < 17) return { key: 'day',      labels: ['Pool', 'Exterior', 'Rooms'] };
  if (hour >= 17 && hour < 20) return { key: 'golden',   labels: ['Terrace', 'Restaurant', 'Exterior'] };
  return                          { key: 'night',    labels: ['Restaurant', 'Rooms', 'Apartment'] };
};

const Hero = () => {
  const [currentImage, setCurrentImage] = useState(0);
  const [heroImages, setHeroImages] = useState(fallbackHeroImages);
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [guests, setGuests] = useState(2);
  const [isSearching, setIsSearching] = useState(false);
  const [searchResult, setSearchResult] = useState(null);
  
  const { updateBookingDates } = useBooking();
  const { t, language } = useLanguage();
  const navigate = useNavigate();

  // Track page view on mount
  useEffect(() => {
    trackPageView('homepage');
  }, []);

  // Load real gallery images to use as hero slides.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/public/gallery`);
        const items = Array.isArray(response.data) ? response.data : [];
        if (items.length === 0 || cancelled) return;

        // Pick one image per preferred category first, then fill up to MAX_HERO_IMAGES with the rest.
        const byCategory = {};
        items.forEach((it) => {
          if (!byCategory[it.category]) byCategory[it.category] = [];
          byCategory[it.category].push(it);
        });

        const picked = [];
        const usedIds = new Set();
        // Prefer categories that match the current time-of-day bucket so the
        // first-impression frame feels like "now" — pool at 3pm, restaurant
        // at night, terrace at golden hour.
        const bucket = heroBucket(new Date().getHours());
        const preferredOrder = [...bucket.labels, ...HERO_CATEGORY_ORDER.filter((c) => !bucket.labels.includes(c))];
        preferredOrder.forEach((cat) => {
          const first = (byCategory[cat] || [])[0];
          if (first && !usedIds.has(first.id)) {
            picked.push(first.image);
            usedIds.add(first.id);
          }
        });
        // Fill remaining slots with any other gallery images
        for (const it of items) {
          if (picked.length >= MAX_HERO_IMAGES) break;
          if (!usedIds.has(it.id)) {
            picked.push(it.image);
            usedIds.add(it.id);
          }
        }
        if (picked.length > 0 && !cancelled) {
          setHeroImages(picked);
          setCurrentImage(0);
        }
      } catch (err) {
        // Silently fall back to hardcoded heroImages — not critical.
        console.warn('[hero] gallery fetch failed, using fallback images:', err.message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % heroImages.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [heroImages.length]);

  const scrollToRooms = () => {
    const element = document.querySelector('#rooms');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleCheckAvailability = async () => {
    if (!checkIn || !checkOut) {
      alert('Please select check-in and check-out dates');
      return;
    }

    const start = new Date(checkIn);
    const end = new Date(checkOut);
    if (end <= start) {
      alert('Check-out date must be after check-in date');
      return;
    }

    setIsSearching(true);
    setSearchResult(null);

    updateBookingDates(checkIn, checkOut, guests);

    try {
      const response = await axios.get(`${BACKEND_URL}/api/beds24/properties`);
      
      if (response.data.success) {
        const nights = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
        
        setSearchResult({
          available: true,
          nights: nights,
          checkIn: checkIn,
          checkOut: checkOut,
          guests: guests
        });
        
        setTimeout(() => {
          document.querySelector('#rooms')?.scrollIntoView({ behavior: 'smooth' });
        }, 500);
      }
    } catch (error) {
      console.error('Availability check error:', error);
      const nights = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
      setSearchResult({
        available: true,
        nights: nights,
        checkIn: checkIn,
        checkOut: checkOut,
        guests: guests
      });
      setTimeout(() => {
        document.querySelector('#rooms')?.scrollIntoView({ behavior: 'smooth' });
      }, 500);
    } finally {
      setIsSearching(false);
    }
  };

  const today = new Date().toISOString().split('T')[0];

  return (
    <section className="relative h-screen min-h-[700px] overflow-hidden" aria-label="Hotel Uma Premium - 4 star hotel in Ulcinj with swimming pool near Velika Plaza">
      {/* SEO: Hidden but crawlable content for search engines */}
      <h2 className="sr-only">Best 4-Star Hotel in Ulcinj with 3 Swimming Pools - Near Velika Plaza Beach</h2>
      {/* Background Images with lazy loading */}
      {heroImages.map((image, index) => (
        <div
          key={image}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            currentImage === index ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <img
            src={optimized(image, 1600)}
            srcSet={srcSetFor(image, [800, 1200, 1600])}
            sizes="100vw"
            alt={`Hotel Uma Premium - 4 star hotel with swimming pool in Ulcinj near Velika Plaza - Image ${index + 1}`}
            loading={index === 0 ? "eager" : "lazy"}
            decoding="async"
            fetchPriority={index === 0 ? "high" : "low"}
            className="w-full h-full object-cover"
          />
        </div>
      ))}

      {/* Gradient Overlays */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-900/60 via-slate-900/30 to-slate-900/70" />
      <div className="absolute inset-0 bg-gradient-to-r from-slate-900/40 to-transparent" />

      {/* Best Price Guarantee Banner - Desktop only to avoid mobile overlap */}
      <div className="absolute top-20 left-0 right-0 z-10 hidden md:block">
        <div className="max-w-7xl mx-auto px-6 flex flex-wrap items-center gap-3">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-emerald-600 to-emerald-500 px-5 py-3 rounded-full shadow-lg">
            <Shield className="w-5 h-5 text-white" />
            <span className="text-white font-bold text-sm">{t('bestPrice.badge')}</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="relative h-full flex flex-col justify-center items-start max-w-7xl mx-auto px-6 pt-16">
        <div className="max-w-2xl">
          {/* Rating & Location Badges */}
          <div className="flex flex-wrap items-center gap-3 mb-6 animate-fade-in">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
              <MapPin className="w-4 h-4 text-amber-400" />
              <span className="text-white/90 text-sm font-medium">Ulcinj, Montenegro</span>
            </div>
            <div className="inline-flex items-center gap-2 bg-amber-500/90 px-4 py-2 rounded-full">
              <Star className="w-4 h-4 text-white fill-white" />
              <span className="text-white text-sm font-medium">4-Star Boutique Hotel</span>
            </div>
          </div>

          {/* Main Heading */}
          <h1 className="text-4xl sm:text-5xl md:text-7xl font-serif text-white mb-4 leading-tight animate-slide-up">
            {hotelInfo.name}
          </h1>

          {/* Tagline */}
          <p className="text-xl md:text-2xl text-amber-300/90 font-light mb-6 animate-slide-up animation-delay-100">
            {t('hero.tagline')}
          </p>

          {/* Description - shorter on mobile */}
          <p className="text-white/80 text-base md:text-lg leading-relaxed mb-8 max-w-xl animate-slide-up animation-delay-200 line-clamp-3 md:line-clamp-none">
            {t('hero.description')}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-wrap gap-3 md:gap-4 animate-slide-up animation-delay-300">
            <Button
              onClick={scrollToRooms}
              className="bg-amber-700 hover:bg-amber-800 text-white px-6 py-4 md:px-8 md:py-6 text-base md:text-lg rounded-full shadow-2xl hover:shadow-amber-500/25 transition-all duration-300 transform hover:-translate-y-1"
            >
              {t('hero.exploreRooms')}
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate(langPath(language, 'gallery'))}
              data-testid="hero-view-gallery"
              className="border-2 border-white/50 text-white hover:bg-white hover:text-slate-900 px-6 py-4 md:px-8 md:py-6 text-base md:text-lg rounded-full transition-all duration-300 bg-transparent"
            >
              {t('hero.viewGallery')}
            </Button>
          </div>
        </div>

        {/* Quick Booking Card */}
        <div className="hidden lg:block absolute bottom-24 right-6 bg-white/95 backdrop-blur-xl rounded-2xl p-6 shadow-2xl animate-fade-in animation-delay-500 w-[420px]">
          <h3 className="text-slate-800 font-semibold mb-4 flex items-center gap-2">
            <Search className="w-5 h-5 text-amber-600" />
            {t('hero.checkAvailability')}
          </h3>
          
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-slate-500 mb-1">{t('hero.checkIn')}</label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-amber-500" />
                  <Input
                    type="date"
                    value={checkIn}
                    onChange={(e) => setCheckIn(e.target.value)}
                    min={today}
                    className="pl-10 border-slate-200 focus:border-amber-500 text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">{t('hero.checkOut')}</label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-amber-500" />
                  <Input
                    type="date"
                    value={checkOut}
                    onChange={(e) => setCheckOut(e.target.value)}
                    min={checkIn || today}
                    className="pl-10 border-slate-200 focus:border-amber-500 text-sm"
                  />
                </div>
              </div>
            </div>
            
            <div>
              <label className="block text-xs text-slate-500 mb-1">{t('hero.guests')}</label>
              <div className="relative">
                <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-amber-500" />
                <select
                  value={guests}
                  onChange={(e) => setGuests(parseInt(e.target.value))}
                  className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-md focus:outline-none focus:border-amber-500 text-sm"
                >
                  {[1, 2, 3, 4, 5, 6].map((num) => (
                    <option key={num} value={num}>{num} {num > 1 ? t('hero.guests') : t('hero.guest')}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {searchResult && (
            <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-200">
              <p className="text-green-700 text-sm font-medium">
                ✓ {t('hero.roomsAvailable')} {searchResult.nights} {searchResult.nights > 1 ? t('hero.nights') : t('hero.night')}
              </p>
              <p className="text-green-600 text-xs mt-1">
                {t('hero.scrollForPrices')}
              </p>
            </div>
          )}

          <Button 
            onClick={handleCheckAvailability}
            disabled={isSearching}
            className="w-full mt-4 bg-amber-700 hover:bg-amber-800 text-white rounded-lg py-3 flex items-center justify-center gap-2"
          >
            {isSearching ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {t('hero.searching')}
              </>
            ) : (
              <>
                <Search className="w-4 h-4" />
                {t('hero.searchAvailability')}
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <ChevronDown className="w-8 h-8 text-white/60" />
      </div>

      {/* Image Indicators */}
      <div className="absolute bottom-8 right-8 flex gap-2">
        {heroImages.map((image, index) => (
          <button
            key={image}
            type="button"
            onClick={() => setCurrentImage(index)}
            aria-label={`Photo ${index + 1} of ${heroImages.length}`}
            aria-current={currentImage === index}
            className="p-2 -m-1 flex items-center"
          >
            <span className={`block h-2 rounded-full transition-all duration-300 ${currentImage === index ? 'w-8 bg-amber-400' : 'w-2 bg-white/50 hover:bg-white/80'}`} />
          </button>
        ))}
      </div>
    </section>
  );
};

export default Hero;
